import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Analyse } from 'r/types';
import { captureException, showNotification } from 'r/utils';
import { buildFilterString, type FilterCondition } from 'r/atoms/utils';
import {
  fetchApiRaw,
  POST_ANALYSES_VERIFY_CMS,
  POST_ANALYSES,
  POST_TWEET,
  POST_ANALYSES_CMS,
  POST_TWEET_CMS,
  GET_ANALYSES,
  PUT_ANALYSES,
} from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';

// Type for fetchApiRaw errors
interface FetchApiError extends Error {
  fromFetchApi: boolean;
  response?: unknown;
  body?: {
    message?: string;
    [key: string]: unknown;
  };
  extra?: {
    responseBody?: unknown;
    code?: number;
    url?: string;
    options?: unknown;
  };
}

/**
 * Mutation hook for verifying an analysis
 */
export function useVerifyAnalysis() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async (analysisId: string) => {
      return await fetchApiRaw(
        POST_ANALYSES_VERIFY_CMS,
        { body: [analysisId] },
        accessTokens.cms,
      );
    },
    onMutate: async (analysisId) => {
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['cms-analyses'] });

      // Snapshot previous value for rollback
      const previousData = queryClient.getQueryData(['cms-analyses']);

      // Optimistically update the cache
      queryClient.setQueryData<{ data: Analyse[] }>(['cms-analyses'], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((analysis: Analyse) =>
            analysis.id === analysisId ?
              { ...analysis, verifiedBy: 'optimistic-user' }
            : analysis,
          ),
        };
      });

      return { previousData };
    },
    onSuccess: () => {
      showNotification('success', 'Analysis verified successfully');
      // Invalidate to refetch with actual data
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
    },
    onError: (error, analysisId, context) => {
      // Rollback optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(['cms-analyses'], context.previousData);
      }
      captureException(error);
      showNotification('error', 'Failed to verify analysis');
    },
  });
}

/**
 * Mutation hook for adding analysis to sanitizer
 */
export function useAddToSanitizer() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async (analysis: Analyse) => {
      await fetchApiRaw(POST_TWEET, {
        body: { tweetID: analysis.tweetID },
      });
      try {
        return await fetchApiRaw(
          POST_ANALYSES,
          {
            body: {
              tweetID: analysis.tweetID,
              pair: analysis.pair,
              isPredictionUp: analysis.isPredictionUp,
              memeURL: analysis.memeURL,
            },
          },
          accessTokens.default,
        );
      } catch (error) {
        const fetchError = error as FetchApiError;
        if (fetchError?.body?.message !== 'SERVER_ERROR') throw error;

        // Soft-deleted duplicate — find it with paranoid=false and restore
        const filterConditions: Record<string, FilterCondition> = {};

        filterConditions.deletedAt = { $ne: null };
        filterConditions.tweetID = analysis.tweetID;
        const result = await fetchApiRaw(GET_ANALYSES, {
          queryParams: {
            take: 1,
            filter: buildFilterString(filterConditions),
            paranoid: false,
          },
        });

        const existing = result?.data?.[0];
        if (!existing) throw error;

        return await fetchApiRaw(
          PUT_ANALYSES,
          {
            urlParams: [existing.id],
            body: {
              pair: analysis.pair,
              isPredictionUp: analysis.isPredictionUp,
              memeURL: analysis.memeURL,
              deletedAt: null,
            },
          },
          accessTokens.default,
        );
      }
    },
    onSuccess: () => {
      showNotification('success', 'Analysis saved to Sanitizer successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
      queryClient.invalidateQueries({ queryKey: ['play-analyses-sanitizer'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to save analysis to Sanitizer');
    },
  });
}

/**
 * Mutation hook for creating a manual analysis
 */
export function useCreateManualAnalysis() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async ({
      tweetID,
      pair,
      isPredictionUp,
    }: {
      tweetID: string;
      pair: string;
      isPredictionUp: boolean;
    }) => {
      await fetchApiRaw(
        POST_TWEET_CMS,
        { body: { tweetID } },
        accessTokens.cms,
      );

      return await fetchApiRaw(
        POST_ANALYSES_CMS,
        {
          body: {
            tweetID,
            pair,
            isPredictionUp,
          },
        },
        accessTokens.cms,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Analysis saved successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to save analysis');
    },
  });
}
